<template>
  <div class="model-detail">
    <template v-if="model">
      <header class="model-detail__header">
        <slot name="header">
          <div>
            <h1>
              {{ model.name }}
              <span
                v-if="isSimulationGame"
                class="modelentry__titlenotice"
              >
                ({{ $t("words.businessGame", 1) }})
              </span>
              <span v-if="locked" class="modelentry__titlenotice">
                ({{ $t("labels.locked") }})
              </span>
            </h1>
            <formatted-text :value="model.description" />
          </div>
        </slot>
      </header>

      <div class="mt-4 model-detail__buttons">
        <router-link
          v-if="editorRoute"
          class="button button_large"
          :to="editorRoute"
          :target="envOptions.openModelsInNewTab ? '_blank' : undefined"
        >
          {{ $t("labels.openModel") }}
        </router-link>

        <div class="model-detail__extrabuttons">
          <slot name="buttons"></slot>

          <button
            v-if="canFork"
            type="button"
            class="button button_secondary model-detail__button"
            @click="data.isDuplicating = true"
          >
            {{ $t("labels.duplicate") }}
          </button>
          <button
            v-if="canDownload"
            :disabled="downloading"
            type="button"
            class="button button_secondary model-detail__button"
            @click="downloadModel()"
          >
            <template v-if="downloading">
              {{ $t("texts.loading") }}
              <loading-spinner size="small" class="ml-2" />
            </template>
            <template v-else>
              {{ $t("labels.download") }}
            </template>
          </button>
        </div>
      </div>

      <model-duplicate-popup
        :visible="data.isDuplicating"
        :model="model"
        @close="data.isDuplicating = false"
      />

      <div v-if="showModifiedAt" class="mb-4 model-detail__date">
        <div>{{ modifiedAtString }}</div>
      </div>

      <slot name="main"> </slot>

      <!--<pre>{{ model }}</pre>-->
    </template>
    <template v-else>
      <error-page-content
        :title="$t('errors.model_not_found')"
        :status="404"
        error-code="model_not_found"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import envOptions from "@/helpers/Env";
import { formatDate } from "@/i18n";
import { downloadModelFile } from "@/api/SaveService";

import LoadingSpinner from "@core/ui/LoadingSpinner.vue";
import FormattedText from "@core/ui/FormattedText.vue";
import ErrorPageContent from "@core/errors/ErrorPageContent.vue";
import ModelDuplicatePopup from "@manager/workspacemodels/ModelDuplicatePopup.vue";

import { useModels } from "@/api/models.store";
import { usePublicModels } from "@/api/publicModels.store";
import { useSharedModels } from "@/api/sharedModels.store";
import { showError } from "@core/ui/alert/alert";
import { useLicense } from "@/api/license.store";
import type { Model } from "@/api/Model";
import type { Workspace } from "@/api/Workspace";
import type { PublicModel } from "@/api/PublicModel";
import type { SharedModel } from "@/api/SharedModel";

const props = withDefaults(defineProps<{
  model: Model | PublicModel | SharedModel | null;
  editorRoute: { name: string, params?: Record<string, string | number | undefined> } | null;
  modelWorkspace?: Workspace | null;
  showModifiedAt?: boolean;
}>(), {
  model: null,
  editorRoute: null,
  modelWorkspace: null,
  showModifiedAt: true
});

const { t } = useI18n();

const license = useLicense();
const { getModelData: getWorkspaceModelData } = useModels();
const { getPublicModelData } = usePublicModels();
const { getSharedModelData } = useSharedModels();

const data = reactive({
  isDuplicating: false
});

const modelPermissions = computed(() => {
  return (
    props.modelWorkspace?.modelpermissions ?? props.model?.permissions ?? {}
  );
});

const canFork = computed(() => {
  return modelPermissions.value?.canFork;
});

const locked = computed(() => {
  if (props.model == null) return false;
  if (!("isLocked" in props.model)) return false;
  return props.model?.isLocked;
});

const isSimulationGame = computed(() => {
  if (props.model == null) return false;
  if (!("isSimulationGame" in props.model)) return false;
  return props.model?.isSimulationGame;
});

const modifiedAtString = computed(() => {
  if (!props.model?.modified) return "";
  const date = new Date(props.model.modified);
  const dateFormatted = formatDate(date, "ff");

  const lastVersion = "lastVersion" in props.model ? props.model.lastVersion : null;
  const creator = "creator" in props.model ? props.model.creator : null;

  const editedBy = lastVersion?.creator?.name ?? creator?.name;

  if (editedBy) {
    return capitalizeFirstLetter(
      t("labels.modifiedAtExactUser", {
        datetime: dateFormatted,
        username: editedBy
      })
    );
  }

  return capitalizeFirstLetter(
    t("labels.modifiedAtExact", {
      datetime: dateFormatted
    })
  );
});


// =============================
// DOWNLOAD

const canDownload = computed(() => {
  if (license.limits.canDownload === false) return false;
  return modelPermissions.value?.canDownload;
});

const downloading = ref(false);

async function downloadModel(name: string | null = null) {
  if (props.model == null) {
    showError(t("errors.notFound"));
    return;
  }
  if (downloading.value) return;

  downloading.value = true;

  try {
    const modelData = await getModelData();
  
    if (modelData.data == null) {
      showError(t("errors.noModeldata"));
      return;
    }
  
    return downloadModelFile(props.model, modelData, {
      name: name || props.model.name,
      isSimulationGame: isSimulationGame.value || false
    });
  } catch (error) {
    showError(t("errors.downloadFailed"));
  } finally {
    downloading.value = false;
  }
}

/**
 * Get the correct model data depending on the model type
 */
function getModelData() {
  if (props.model == null) {
    throw new Error("Model is not defined");
  }
  if (props.modelWorkspace == null) {
    throw new Error("Model workspace is not defined");
  }

  switch (props.model.type) {
    case "workspace_model":
      return getWorkspaceModelData(
        props.modelWorkspace.id,
        props.model.id
      );

    case "shared_model":
      return getSharedModelData(props.model.id as number);

    case "public_model":
      return getPublicModelData(props.model.id as string);
  }
}

// =============================
// UTILS

function capitalizeFirstLetter(word: string) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}
</script>

<style lang="scss">
.model-detail {
  padding-bottom: 1.5rem;

  padding: 2rem;
  background-color: var(--color-background-deep);
  border-radius: 5px;
  box-shadow: 0 2px 5px var(--color-shadow-1);
  container-type: inline-size;

  &__header {
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--color-border);
    display: grid;
    grid-template-columns: minmax(0, 1fr) 10rem;
    gap: 1rem;
    justify-content: space-between;
    align-items: flex-start;

    h1 {
      margin-top: 0;
    }
  }

  &__buttons {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 0.5rem;
    align-items: flex-start;
    @container (min-width: 500px) {
      grid-template-columns: max-content minmax(0, 1fr);
      align-items: flex-start;
    }
  }
  &__extrabuttons {
    display: flex;
    gap: 0.5rem;
    margin-left: auto;
    flex-wrap: wrap;
    justify-content: flex-end;
  }
  &__button {
    padding-inline: 1.75em;
  }

  &__date {
    padding: 1rem 0;
    margin-top: 2rem;
  }
}
</style>
